import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Sparkles, X, Users, Languages, Video, Palette, Calendar, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';

import { useTranslation } from 'react-i18next';

// NOTE: localStorage here stores only non-sensitive UI preferences (dismiss state, rotation index).
// Auth tokens are managed via httpOnly cookies — never stored in localStorage.
const STORAGE_KEYS = {
  dismissed: 'eili-highlight-dismissed',
  lastShown: 'eili-highlight-last-shown',
  featureIdx: 'eili-highlight-feature-idx',
};

const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

const FEATURES = [
  {
    key: 'commentators',
    icon: Users,
    title: 'Commentary Library',
    desc: 'Access insights from Charles Spurgeon, Matthew Henry, and more alongside your study.',
    preview: [
      '"The grace of God is the foundation of all our hope." — Charles Spurgeon',
      '"This verse speaks to the...',
      '"In the original context, Paul...',
    ],
  },
  {
    key: 'greek_analysis',
    icon: Languages,
    title: 'Greek-English Analysis',
    desc: 'See original Greek words, transliterations, and detailed lexical commentary for key terms.',
    preview: [
      'agape (ἀγάπη) — unconditional love',
      'pistis (πίστις) — faith, trust',
      'charis (χάρις) — grace, favor',
    ],
  },
  {
    key: 'historical_context',
    icon: Landmark,
    title: 'Historical & Cultural Context',
    desc: 'Get deeply researched background on the historical setting, cultural practices, and authorial context of passages.',
    preview: [
      'Setting: 1st century Roman-occupied Palestine',
      'Audience: Jewish Christians facing persecution',
      'Source: Josephus, Antiquities XIV',
    ],
  },
  {
    key: 'youtube_analysis',
    icon: Video,
    title: 'YouTube Analysis',
    desc: 'Paste a sermon or teaching URL. Eili evaluates biblical soundness and highlights key points.',
    preview: [
      'Biblical Accuracy: 92%',
      'Key Themes: Grace, Redemption',
      'Scripture References: Romans 8:28...',
    ],
  },
  {
    key: 'themes',
    icon: Palette,
    title: 'All Themes',
    desc: 'Personalize your study environment with 6 beautiful themes in both light and dark modes.',
    colors: ['#A78BFA', '#F59E0B', '#38BDF8', '#4ADE80', '#D97706'],
  },
  {
    key: 'recap',
    icon: Calendar,
    title: 'Weekly Recap',
    desc: 'Receive a curated summary of your weekly study topics and key insights every week.',
    preview: [
      'This week you explored:',
      '  Salvation in Romans 3-5',
      '  The Sermon on the Mount',
    ],
  },
];

export default function PremiumHighlight({ onUpgrade }) {
  const {
    t
  } = useTranslation();

  const { isPaid, tier } = useSubscription();
  const [visible, setVisible] = useState(false);
  const [feature, setFeature] = useState(null);

  useEffect(() => {
    if (isPaid || tier === null) return;

    const dismissed = localStorage.getItem(STORAGE_KEYS.dismissed) === 'true';
    if (dismissed) return;

    const lastShown = parseInt(localStorage.getItem(STORAGE_KEYS.lastShown) || '0', 10);
    const now = Date.now();
    if (now - lastShown < TWO_DAYS_MS) return;

    const idx = parseInt(localStorage.getItem(STORAGE_KEYS.featureIdx) || '0', 10);
    const nextIdx = idx % FEATURES.length;
    setFeature(FEATURES[nextIdx]);
    setVisible(true);

    localStorage.setItem(STORAGE_KEYS.lastShown, String(now));
    localStorage.setItem(STORAGE_KEYS.featureIdx, String(nextIdx + 1));
  }, [isPaid, tier]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
  }, []);

  const handleNeverShow = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.dismissed, 'true');
    setVisible(false);
  }, []);

  const handleUnlock = useCallback(() => {
    if (feature && onUpgrade) onUpgrade(feature.key);
  }, [feature, onUpgrade]);

  if (!visible || !feature) return null;

  const Icon = feature.icon;

  return (
    <div
      className="w-full animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="premium-highlight-card"
    >
      <div
        className="relative rounded-2xl overflow-hidden p-4"
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid var(--glass-border)',
        }}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-full transition hover:bg-white/10"
          data-testid="premium-highlight-close"
        >
          <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
        </button>

        <div className="flex gap-3 items-start">
          {/* Icon */}
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: 'var(--theme-accent)', opacity: 0.9 }}
          >
            <Icon className="h-4 w-4 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            {/* Title */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                {feature.title}
              </span>
              <span
                className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide flex items-center gap-0.5"
                style={{ background: 'var(--theme-accent)', color: '#fff' }}
              >
                <Lock className="h-2 w-2" />
                {t('generated.premiumhighlight_pro_77ed')}
              </span>
            </div>

            {/* Description */}
            <p className="text-xs leading-relaxed mb-2.5" style={{ color: 'var(--theme-textSecondary)' }}>
              {feature.desc}
            </p>

            {/* Blurred Preview */}
            {feature.preview && (
              <div
                className="rounded-lg p-2.5 mb-3 select-none relative overflow-hidden"
                style={{ background: 'var(--glass-bg-active)' }}
              >
                <div className="blur-[2px] pointer-events-none">
                  {feature.preview.map((line, i) => (
                    <p key={i} className="text-[11px] leading-5 truncate" style={{ color: 'var(--theme-textSecondary)' }}>
                      {line}
                    </p>
                  ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock className="h-5 w-5" style={{ color: 'var(--theme-accent)', opacity: 0.7 }} />
                </div>
              </div>
            )}

            {/* Theme colors preview */}
            {feature.colors && (
              <div className="flex gap-2 mb-3 items-center">
                {feature.colors.map((c, i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full blur-[1px]"
                    style={{ background: c, border: '2px solid var(--glass-border)' }}
                  />
                ))}
                <div className="ml-1">
                  <Lock className="h-4 w-4" style={{ color: 'var(--theme-accent)', opacity: 0.7 }} />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                onClick={handleUnlock}
                className="rounded-full px-4 h-8 text-xs font-medium gap-1.5"
                style={{ background: 'var(--theme-accent)', color: '#fff' }}
                data-testid="premium-highlight-unlock"
              >
                <Sparkles className="h-3 w-3" />
                {t('generated.premiumhighlight_unlock_46a4')}
              </Button>
              <button
                onClick={handleNeverShow}
                className="text-[10px] font-medium transition hover:underline"
                style={{ color: 'var(--theme-textSecondary)' }}
                data-testid="premium-highlight-never"
              >
                {t('generated.premiumhighlight_don_t_show_4ba4')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
